import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, ClosableDrawer } from '@atoms/layout';
import { InputAdornment, Stack, styled, Typography } from '@mui/material';
import { Button } from '@atoms/buttons';
import { useForm } from 'react-hook-form';
import { FormAutocompleteMultiple, FormBlock, FormPhoneNumber, FormSelect, FormTextField, Form } from '@molecules/form';
import { useNotification } from '@libs/snackbar';
import { ContactInfo } from '@libs/models/common/contactInfo';
import { Place } from '@libs/models/common';
import { emailPattern } from '@libs/forms';
import { useLinesOfBusinessListStore } from '@libs/store/linesOfBusiness';
import { AddressAutocomplete, Place as AutocompletePlace } from '@atoms/inputs';
import { useFacilityEditStore } from '@libs/store/facilities/editDrawerStore';
import { FacilityUpdateData } from '@libs/models/facilities';
import { useFacilityTypesListStore } from '@libs/store/facilityTypes/listStore';

type EditFacilityDrawerProps = {
  onSaveCall?: () => void;
  localizationSubset: string;
};

const numberOfEmployeesRanges = ['1-100', '101-250', '251-500', '500+'];

export function EditFacilityDrawer({ onSaveCall, localizationSubset }: EditFacilityDrawerProps) {
  const intl = useIntl();
  const { showError } = useNotification();

  const {
    drawerOpen,
    drawerMode,
    closeDrawer,
    addFacility,
    updateFacility,
    currentEntityId: currentId,
  } = useFacilityEditStore();

  const {
    linesOfBusiness: linesOfBusinessList,
    loadingList: linesOfBusinessListLoading,
    load: loadLinesOfBusiness,
  } = useLinesOfBusinessListStore();

  const {
    facilityTypes: facilityTypesList,
    loadingList: facilityTypesListLoading,
    load: loadFacilityTypes,
  } = useFacilityTypesListStore();

  const isAddDialog = drawerMode == 'add';
  localizationSubset = isAddDialog ? `${localizationSubset}.add` : `${localizationSubset}.edit`;
  const open = drawerOpen;

  const loading = linesOfBusinessListLoading || facilityTypesListLoading;

  useEffect(() => {
    if (!linesOfBusinessList.length) {
      loadLinesOfBusiness();
    }
  }, [loadLinesOfBusiness, linesOfBusinessList]);

  useEffect(() => {
    if (!facilityTypesList.length) {
      loadFacilityTypes();
    }
  }, [loadFacilityTypes, facilityTypesList]);

  const methods = useForm<FacilityFormData>({
    defaultValues: {
      name: '',
      domainName: '',
      facilityTypes: [],
      numberOfEmployees: '',
      contactFirstName: '',
      contactLastName: '',
      contactPhoneNumber: '',
      addressAddressFirst: '',
      addressAddressSecond: '',
      addressCity: '',
      addressState: '',
      addressZipCode: '',
    },
  });
  const {
    setValue,
    setError,
    watch,
    formState: { errors },
  } = methods;

  const handleClose = () => {
    closeDrawer();
    methods.reset();
  };
  const handleSubmit = async (isPublish: boolean, data: FacilityFormData) => {
    const sendingData: FacilityUpdateData = {
      id: '',
      name: data.name,
      domainName: data.domainName,
      numberOfEmployees: {
        minValue: 0,
        maxValue: 0,
      },
      facilityTypes: data.facilityTypes,
      contact: {
        email: data.contactEmail,
        firstName: data.contactFirstName,
        phoneNumber: data.contactPhoneNumber,
        lastName: data.contactLastName,
      },
      address: {
        addressFirst: data.addressAddressFirst,
        addressSecond: data.addressAddressSecond,
        city: data.addressCity,
        state: data.addressState,
        zipCode: data.addressZipCode,
      },
    };

    try {
      if (isAddDialog) {
        await addFacility(sendingData);
      } else {
        await updateFacility(currentId, sendingData);
      }

      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      }
    }
  };

  const handleAddActivate = async () => {};
  const handleAdd = async () => {};

  const facilityTypes = watch('facilityTypes');
  const address1 = watch('addressAddressFirst');
  const agencyName = watch('name');
  useEffect(() => {
    if (agencyName) {
      setValue('domainName', agencyName.replace(/\s+/g, '-').toLowerCase());
    }
  }, [agencyName, setValue]);

  return (
    <ClosableDrawer
      open={open}
      loading={loading}
      onClose={handleClose}
      controls={
        <Stack direction={'row-reverse'} spacing={2} sx={(theme) => ({ paddingBottom: theme.spacing(1) })}>
          <MenuButton variant="text" onClick={handleAddActivate} disabled={loading}>
            <FormattedMessage id={`${localizationSubset}.menuButtons.addActivate.label`} />
          </MenuButton>
          <MenuButton variant="text" color={'error'} onClick={handleAdd} disabled={loading}>
            <FormattedMessage id={`${localizationSubset}.menuButtons.add.label`} />
          </MenuButton>
          <Box flex={1} />
          <MenuButton
            onClick={handleClose}
            sx={(theme) => ({ color: theme.palette.text.secondary })}
            disabled={loading}
          >
            <FormattedMessage id={`${localizationSubset}.menuButtons.back.label`} />
          </MenuButton>
        </Stack>
      }
    >
      <Stack direction={'column'} sx={{ height: '100%' }} spacing={2} width={{ xs: '300px', sm: '600px' }}>
        {/* Header */}
        <Stack direction={'row'}>
          <Typography variant="h5">
            <FormattedMessage id={`${localizationSubset}.dialogHeading`} />
          </Typography>
          <Box flex={1} />
        </Stack>
        <Form formUtils={methods} style={{ height: '100%' }} onSubmit={handleSubmit.bind(handleSubmit, false)}>
          {/* Basics */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.details.sectTitle`} />}>
            <Stack direction={'row'} spacing={2}>
              <FormTextField
                disabled={loading}
                label={<FormattedMessage id={`${localizationSubset}.details.agencyName.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.details.agencyName.placeholder`,
                })}
                required
                fullWidth
                name="name"
              />
              <FormTextField
                disabled={loading}
                required
                label={<FormattedMessage id={`${localizationSubset}.details.domainName.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.details.domainName.placeholder`,
                })}
                fullWidth
                name="domainName"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <FormattedMessage id={`${localizationSubset}.details.domainName.endAdornment`} />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            <Stack direction={'row'} spacing={2}>
              <FormAutocompleteMultiple
                options={facilityTypesList.map((lob) => ({
                  value: lob.id,
                  label: lob.label,
                  key: lob.id,
                }))}
                disabled={loading}
                required
                fullWidth
                label={<FormattedMessage id={`${localizationSubset}.details.facilityTypes.label`} />}
                textFieldProps={{
                  placeholder:
                    facilityTypes.length === 0
                      ? intl.formatMessage({
                          id: `${localizationSubset}.details.facilityTypes.placeholder`,
                        })
                      : undefined,
                }}
                disableCloseOnSelect
                name="facilityTypes"
                requiredMessage={intl.formatMessage({
                  id: `${localizationSubset}.details.facilityTypes.requiredMessage`,
                })}
              />
            </Stack>
            <Stack direction={'row'} spacing={2}>
              <FormSelect
                disabled={loading}
                options={numberOfEmployeesRanges.map((noe) => ({
                  value: noe,
                  label: noe,
                  key: noe,
                }))}
                label={<FormattedMessage id={`${localizationSubset}.details.numberOfEmployees.label`} />}
                fullWidth
                required
                name="numberOfEmployees"
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.details.numberOfEmployees.placeholder`,
                })}
              />
            </Stack>
          </FormBlock>
          {/* Contact */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.contact.sectTitle`} />}>
            <Stack direction={'row'} spacing={2}>
              <FormTextField
                disabled={loading}
                required
                label={<FormattedMessage id={`${localizationSubset}.contact.firstName.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.firstName.placeholder`,
                })}
                fullWidth
                name="firstName"
              />
              <FormTextField
                disabled={loading}
                required
                label={<FormattedMessage id={`${localizationSubset}.contact.lastName.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.lastName.placeholder`,
                })}
                fullWidth
                name="lastName"
              />
            </Stack>
            <Stack direction={'row'} spacing={2}>
              <FormTextField
                disabled={loading}
                required
                label={<FormattedMessage id={`${localizationSubset}.contact.email.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.email.placeholder`,
                })}
                fullWidth
                name="email"
                options={{
                  maxLength: 254,
                  pattern: {
                    value: emailPattern,
                    message: intl.formatMessage({
                      id: 'forms.invalidEmail',
                    }),
                  },
                }}
              />
              <FormPhoneNumber
                disabled={loading}
                required
                label={<FormattedMessage id={`${localizationSubset}.contact.phoneNumber.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.phoneNumber.placeholder`,
                })}
                fullWidth
                name="phoneNumber"
              />
            </Stack>
          </FormBlock>

          {/* Address */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.address.sectTitle`} />}>
            <Stack direction={'column'} spacing={1}>
              <Stack direction={'row'} spacing={2}>
                <AddressAutocomplete
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.address.addressAddressFirst.label`} />}
                  fullWidth
                  required
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.address.addressAddressFirst.placeholder`,
                  })}
                  noOptionsText={intl.formatMessage({
                    id: `${localizationSubset}.address.addressAddressFirst.noLocations`,
                  })}
                  {...methods.register('addressAddressFirst', {
                    required: intl.formatMessage({
                      id: 'forms.fieldIsRequired',
                    }),
                  })}
                  onChange={(newValue: AutocompletePlace | null) => {
                    setValue('addressAddressFirst', newValue?.description ?? undefined, {
                      shouldValidate: true,
                    });
                    if (!newValue) {
                      // When clearing the address, leave the city, state and zip
                      return;
                    }
                    setValue('addressCity', newValue?.city, {
                      shouldValidate: true,
                    });
                    setValue('addressState', newValue?.state, {
                      shouldValidate: true,
                    });
                    setValue('addressZipCode', newValue?.zip ?? '', {
                      shouldValidate: true,
                    });
                  }}
                  value={address1}
                  error={errors.addressAddressFirst?.message}
                />
              </Stack>
              <Stack direction={'row'} spacing={2}>
                <FormTextField
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.address.addressAddressSecond.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.address.addressAddressSecond.placeholder`,
                  })}
                  fullWidth
                  name="addressAddressSecond"
                />
              </Stack>
              <Stack direction={'row'} spacing={2}>
                <FormTextField
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.address.addressCity.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.address.addressCity.placeholder`,
                  })}
                  fullWidth
                  name="addressCity"
                />
                <FormTextField
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.address.addressState.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.address.addressState.placeholder`,
                  })}
                  fullWidth
                  name="addressState"
                />
                <FormTextField
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.address.addressZipCode.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.address.addressZipCode.placeholder`,
                  })}
                  fullWidth
                  name="addressZipCode"
                  options={{
                    pattern: {
                      value: /^\d{5}(?:[-\s]\d{4})?$/,
                      message: intl.formatMessage({
                        id: 'forms.invalidZipCode',
                      }),
                    },
                  }}
                />
              </Stack>
            </Stack>
          </FormBlock>
        </Form>
      </Stack>
    </ClosableDrawer>
  );
}
const MenuButton = styled(Button)(({ theme }) => ({
  // minWidth: theme.spacing(16.75),
}));

type FacilityFormData = {
  domainName: FacilityUpdateData['domainName'];
  name: FacilityUpdateData['name'];
  facilityTypes: FacilityUpdateData['facilityTypes'];
  numberOfEmployees: string;
  contactFirstName: ContactInfo['firstName'];
  contactLastName: ContactInfo['lastName'];
  contactPhoneNumber: ContactInfo['phoneNumber'];
  contactEmail: ContactInfo['email'];
  addressAddressFirst: Place['addressFirst'];
  addressAddressSecond: Place['addressSecond'];
  addressCity: Place['city'];
  addressState: Place['state'];
  addressZipCode: Place['zipCode'];
};
